/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect } from "react";
import {
  IconButton,
  Button,
  TextField,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import {
  Edit as EditIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
} from "@mui/icons-material";
import { fetchAndSavePriorityColumns } from "../pages/config/tableHelper";

const MAX_SELECTION_LIMIT = 7;
const PROJECT_ID = localStorage.getItem("project_id"); //15 //

const CATEGORY_DEFINITIONS = [
  {
    name: "Performance",
    keys: [
      "cost",
      "ctr",
      "clicks",
      "impressions",
      "average_cost",
      "average_cpm",
      "average_cpc",
      "interaction_rate",
      "video_views",
      "interactions",
      "interaction_rate",
    ],
  },
  {
    name: "Conversions",
    keys: [
      "conversions",
      "cost_per_conversion",
      "conversion_rate",
      "value_per_conv",
    ],
  },
  {
    name: "Attributes",
    keys: ["campaign_type", "bid_strategy", "opt_score"],
  },
  {
    name: "Competitive",
    keys: ["search_impr_share", "click_share", "quality_score"],
  },
];

const getCategoryForKey = (key) => {
  for (const group of CATEGORY_DEFINITIONS) {
    if (group.keys.includes(key)) {
      return group.name;
    }
  }
  return "Other"; // Fallback
};

const PriorityColumns = () => {
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);

  const [isEditing, setIsEditing] = useState(false);

  const [tempColumns, setTempColumns] = useState([]);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchInput, setShowSearchInput] = useState(false);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (!PROJECT_ID) return;

    const fetchColumns = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          "https://api.confidanto.com/project-columns/get",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ project_id: PROJECT_ID }),
          },
        );

        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0 && data[0].columns) {
            const apiColumns = data[0].columns.map((col) => ({
              ...col,
              category: getCategoryForKey(col.key), // Assign category dynamically
            }));
            setColumns(apiColumns);
          }
        } else {
          console.error("Failed to fetch columns");
        }
      } catch (error) {
        console.error("Error fetching columns:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchColumns();
  }, []);

  useEffect(() => {
    if (isEditing) {
      setTempColumns(JSON.parse(JSON.stringify(columns)));
      const categories = [...new Set(columns.map((c) => c.category))];
      if (categories.length > 0) setExpandedCategory(categories[0]);
    }
  }, [isEditing, columns]);

  useEffect(() => {
    if (showSearchInput && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearchInput]);

  const visibleColumns = columns.filter((c) => c.visible);

  const categories = tempColumns.length
    ? [...new Set(tempColumns.map((col) => col.category))]
    : [];

  const currentSelectionCount = tempColumns.filter((c) => c.visible).length;
  const isLimitReached = currentSelectionCount >= MAX_SELECTION_LIMIT;

  const handleEditOpen = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSearchQuery("");
    setShowSearchInput(false);
  };

  const handleApply = async () => {
    setColumns(tempColumns);
    setIsEditing(false);
    setSearchQuery("");
    setShowSearchInput(false);

    try {
      const payloadColumns = tempColumns.map(({ title, key, visible }) => ({
        title,
        key,
        visible,
      }));

      const response = await fetch(
        "https://api.confidanto.com/project-columns/update",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            project_id: PROJECT_ID,
            columns: payloadColumns,
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Update failed");
      }
      await fetchAndSavePriorityColumns(localStorage.getItem("project_id")); //add project id in production

      console.info("Columns updated and local storage synced successfully.");
    } catch (error) {
      console.error("Error updating columns:", error);
      alert("Failed to save column preferences.");
    }
  };

  const toggleCategory = (cat) => {
    setExpandedCategory(expandedCategory === cat ? null : cat);
  };

  const toggleColumnSelection = (key) => {
    setTempColumns((prev) => {
      const target = prev.find((c) => c.key === key);
      if (
        !target.visible &&
        prev.filter((c) => c.visible).length >= MAX_SELECTION_LIMIT
      ) {
        return prev;
      }

      return prev.map((col) =>
        col.key === key ? { ...col, visible: !col.visible } : col,
      );
    });
  };

  const filteredSearchColumns = tempColumns.filter((col) =>
    col.title.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleSearchResultClick = (key) => {
    const targetCol = tempColumns.find((c) => c.key === key);
    if (!targetCol) return;

    if (!targetCol.visible && isLimitReached) return;

    setTempColumns((prev) =>
      prev.map((col) => (col.key === key ? { ...col, visible: true } : col)),
    );

    setExpandedCategory(targetCol.category);

    setSearchQuery("");
    setShowSearchInput(false);
  };

  const renderViewMode = () => {
    const activeCategories = [
      ...new Set(visibleColumns.map((c) => c.category)),
    ];

    return (
      <div className="bg-white/80 backdrop-blur-md border border-gray-200 shadow-xl rounded-2xl flex flex-col transition-all md:even:min-h-[180px] hover:shadow-2xl">
        <div className="p-5 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h6 className="font-semibold text-lg text-gray-800 tracking-tight">
              Priority Columns
            </h6>
            <p className="text-sm text-gray-500 mt-1">
              Active columns ({visibleColumns.length}/{MAX_SELECTION_LIMIT})
            </p>
          </div>
          <IconButton
            onClick={handleEditOpen}
            className="bg-gray-100 hover:bg-gray-200"
            disabled={loading}
          >
            <EditIcon fontSize="small" className="text-gray-700" />
          </IconButton>
        </div>

        <div className="p-5 space-y-4">
          {loading ? (
            <div className="flex justify-center py-4">
              <CircularProgress size={30} />
            </div>
          ) : (
            <>
              {visibleColumns.length === 0 && (
                <span className="text-gray-400 italic">No columns active.</span>
              )}

              {activeCategories.map((cat) => (
                <div key={cat}>
                  <h6 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                    {cat}
                  </h6>
                  <div className="flex flex-wrap gap-2">
                    {visibleColumns
                      .filter((c) => c.category === cat)
                      .map((col) => (
                        <div
                          key={col.key}
                          className="px-3 py-1 bg-blue-50 text-blue-700 rounded-md text-sm font-medium border border-blue-100"
                        >
                          {col.title}
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    );
  };

  const renderEditMode = () => (
    <div className="bg-white shadow-lg rounded-lg flex flex-col border border-blue-200 relative">
      <div className="h-[60px] border-b border-gray-200 px-4 flex items-center justify-between bg-gray-50 rounded-t-lg">
        {showSearchInput ? (
          <div className="w-full flex items-center animate-fade-in">
            <TextField
              inputRef={searchInputRef}
              fullWidth
              variant="standard"
              placeholder="Find a column..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{ disableUnderline: true, className: "text-sm" }}
            />
            <IconButton
              size="small"
              onClick={() => {
                setShowSearchInput(false);
                setSearchQuery("");
              }}
            >
              <CloseIcon fontSize="small" className="hover:text-red-500" />
            </IconButton>
          </div>
        ) : (
          <>
            <div>
              <h2 className="font-bold text-lg text-gray-700">
                Modify columns
              </h2>
              <p className="text-xs text-gray-500">
                Selected: {currentSelectionCount}/{MAX_SELECTION_LIMIT}
              </p>
            </div>
            <div
              className="flex items-center gap-2 text-gray-500 hover:text-blue-600 cursor-pointer px-2 py-1 rounded hover:bg-gray-200 transition-colors"
              onClick={() => setShowSearchInput(true)}
            >
              <SearchIcon fontSize="small" />
              <span className="text-sm font-medium">Search</span>
            </div>
          </>
        )}
      </div>

      {/* Main Body */}
      <div className="flex flex-col md:flex-row h-[400px]">
        {/* LEFT: Category Selection */}
        <div className="flex-1 border-r border-gray-200 overflow-y-auto p-2">
          {showSearchInput && searchQuery ? (
            // Search Results View
            <div className="space-y-1">
              {filteredSearchColumns.length === 0 ? (
                <div className="p-4 text-center text-gray-400 text-sm">
                  No columns found
                </div>
              ) : (
                filteredSearchColumns.map((col) => {
                  const disabled = !col.visible && isLimitReached;
                  return (
                    <div
                      key={col.key}
                      onClick={() =>
                        !disabled && handleSearchResultClick(col.key)
                      }
                      className={`p-2 rounded text-sm flex justify-between items-center group ${
                        disabled
                          ? "opacity-50 cursor-not-allowed"
                          : "hover:bg-blue-50 cursor-pointer"
                      }`}
                    >
                      <span>{col.title}</span>
                      <span className="text-xs text-gray-400 group-hover:text-blue-400">
                        {col.category}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          ) : (
            // Categories View
            <div className="space-y-2">
              {categories.map((cat) => (
                <div key={cat} className="rounded border border-transparent">
                  <div
                    onClick={() => toggleCategory(cat)}
                    className={`flex justify-between items-center p-2 cursor-pointer rounded hover:bg-gray-100 ${
                      expandedCategory === cat ? "bg-gray-100 font-medium" : ""
                    }`}
                  >
                    <span className="text-sm text-gray-700">{cat}</span>
                    {expandedCategory === cat ? (
                      <ArrowUpIcon fontSize="small" className="text-gray-500" />
                    ) : (
                      <ArrowDownIcon
                        fontSize="small"
                        className="text-gray-500"
                      />
                    )}
                  </div>

                  {/* Columns in Category */}
                  {expandedCategory === cat && (
                    <div className="ml-2 pl-2 border-l-2 border-gray-200 mt-1 space-y-1 mb-2">
                      {tempColumns
                        .filter((c) => c.category === cat)
                        .map((col) => {
                          const isDisabled = !col.visible && isLimitReached;

                          return (
                            <div
                              key={col.key}
                              onClick={() =>
                                !isDisabled && toggleColumnSelection(col.key)
                              }
                              className={`flex items-center p-1.5 rounded transition-colors ${
                                isDisabled
                                  ? "opacity-50 cursor-not-allowed"
                                  : "hover:bg-gray-50 cursor-pointer"
                              }`}
                            >
                              <Checkbox
                                checked={col.visible}
                                disabled={isDisabled}
                                size="small"
                                color="primary"
                                style={{ padding: "2px 8px 2px 2px" }}
                              />
                              <span className="text-sm text-gray-600">
                                {col.title}
                              </span>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="w-full md:w-[40%] bg-gray-50 p-3 overflow-y-auto flex flex-col">
          <div className="mb-2 flex justify-between items-end">
            <h3 className="font-bold text-sm text-gray-700">Your columns</h3>
            <span
              className={`text-xs ${
                isLimitReached ? "text-orange-600 font-medium" : "text-gray-400"
              }`}
            >
              {currentSelectionCount}/{MAX_SELECTION_LIMIT}
            </span>
          </div>

          <div className="space-y-2 flex-1">
            {tempColumns.filter((c) => c.visible).length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-gray-400 text-sm italic border-2 border-dashed border-gray-200 rounded">
                Select columns from the left
              </div>
            ) : (
              tempColumns
                .filter((c) => c.visible)
                .map((col) => (
                  <div
                    key={col.key}
                    className="bg-white p-2 rounded shadow-sm border border-gray-200 flex items-center justify-between group hover:border-blue-300 transition-colors"
                  >
                    <span className="text-sm text-gray-700 pl-1">
                      {col.title}
                    </span>
                    <IconButton
                      size="small"
                      onClick={() => toggleColumnSelection(col.key)}
                      className="opacity-60 group-hover:opacity-100 transition-opacity"
                    >
                      <CloseIcon
                        fontSize="small"
                        className="text-gray-400 hover:text-red-500 w-4 h-4"
                      />
                    </IconButton>
                  </div>
                ))
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-gray-200 flex justify-end gap-2 bg-gray-50 rounded-b-lg">
        <Button
          onClick={handleApply}
          variant="contained"
          color="primary"
          size="small"
          className="normal-case bg-blue-600"
        >
          Apply
        </Button>
        <Button
          onClick={handleCancel}
          variant="text"
          color="inherit"
          size="small"
          className="normal-case text-gray-600"
        >
          Cancel
        </Button>
      </div>
    </div>
  );

  return (
    <div className="mb-10">
      {isEditing ? renderEditMode() : renderViewMode()}
    </div>
  );
};

export default PriorityColumns;
